import { useState, useEffect, useRef } from "react";

export default function VoiceVisualizer({ state = "idle", volume = 0 }) {
  const [bars, setBars] = useState(Array(12).fill(20));
  const animationRef = useRef();

  useEffect(() => {
    if (state === "speaking") {
      const animate = () => {
        setBars(prev => prev.map(() => 
          20 + Math.random() * 80
        ));
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else if (state === "listening") {
      const animate = () => {
        const normalizedVolume = Math.min(volume / 100, 1);
        setBars(prev => prev.map((_, i) => {
          const phase = (Date.now() / 200 + i * 0.3) % (Math.PI * 2);
          return 20 + Math.sin(phase) * 30 + normalizedVolume * 50;
        }));
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else {
      setBars(Array(12).fill(20));
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, volume]);

  // Brand purple color: #6B5B95
  const primaryColor = "#6B5B95";
  const primaryLight = "#8B7BB5";

  return (
    <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-testid="voice-visualizer">
      {/* Outer glow */}
      <div 
        className={`absolute inset-0 rounded-full transition-all duration-500 ${
          state === "idle" ? "opacity-30" :
          state === "listening" ? "opacity-60 animate-pulse-ring" :
          state === "processing" ? "opacity-50" :
          "opacity-80"
        }`}
        style={{
          background: `radial-gradient(circle at center, rgba(107, 91, 149, 0.3) 0%, transparent 70%)`
        }}
      />

      {/* Ripple effect when speaking */}
      {state === "speaking" && (
        <>
          <div 
            className="absolute inset-0 rounded-full animate-ripple"
            style={{ border: `2px solid ${primaryColor}`, opacity: 0.3 }}
          />
          <div 
            className="absolute inset-0 rounded-full animate-ripple"
            style={{ border: `2px solid ${primaryColor}`, opacity: 0.2, animationDelay: "0.5s" }}
          />
        </>
      )}

      {/* Main orb */}
      <div 
        className={`relative w-48 h-48 md:w-60 md:h-60 rounded-full flex items-center justify-center transition-all duration-300 ${
          state === "processing" ? "animate-spin-gradient" : ""
        }`}
        style={{
          background: state === "processing" 
            ? `conic-gradient(from 0deg, ${primaryColor}, ${primaryLight}, ${primaryColor})`
            : `linear-gradient(135deg, ${primaryColor} 0%, #4A3F6B 100%)`,
          boxShadow: state !== "idle" 
            ? `0 0 60px rgba(107, 91, 149, 0.4)` 
            : `0 0 30px rgba(107, 91, 149, 0.2)`
        }}
      >
        {/* Inner circle */}
        <div 
          className={`w-40 h-40 md:w-52 md:h-52 rounded-full bg-background flex items-center justify-center transition-all duration-300 ${
            state === "listening" ? "scale-95" : ""
          }`}
        >
          {/* Waveform visualization */}
          <div className="flex items-center justify-center gap-1 h-20">
            {bars.map((height, i) => (
              <div
                key={i}
                className="w-1.5 md:w-2 rounded-full transition-all"
                style={{
                  height: `${height}%`,
                  background: state === "idle" 
                    ? "rgba(107, 91, 149, 0.3)"
                    : `linear-gradient(to top, ${primaryColor}, ${primaryLight})`,
                  transitionDuration: state === "speaking" ? "50ms" : "150ms"
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        <div 
          className={`w-2 h-2 rounded-full transition-colors ${
            state === "idle" ? "bg-muted-foreground" :
            state === "listening" ? "bg-green-500 animate-pulse" :
            state === "processing" ? "bg-amber-500 animate-pulse" :
            "bg-primary animate-pulse"
          }`}
        />
        <span className="text-sm text-muted-foreground font-medium capitalize">
          {state === "idle" ? "Ready" :
           state === "listening" ? "Listening..." :
           state === "processing" ? "Processing..." :
           "Speaking..."}
        </span>
      </div>
    </div>
  );
}
